import React from 'react';
import { FileTextIcon, CalendarIcon, UserIcon } from 'lucide-react';
import { InvoiceStatusBadge } from './InvoiceStatusBadge';
import type { Invoice } from '@/types/invoice';

interface InvoiceCardProps {
  invoice: Invoice;
  onClick: () => void;
}

export function InvoiceCard({ invoice, onClick }: InvoiceCardProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: invoice.currency || 'INR',
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  return (
    <button
      onClick={onClick}
      className="w-full bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow text-left"
    >
      {/* Header: Invoice Number and Status */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileTextIcon className="w-4 h-4 text-gray-400" />
          <span className="font-semibold text-gray-900">{invoice.invoice_number}</span>
        </div>
        <InvoiceStatusBadge status={invoice.status} />
      </div>

      {/* Client Name */}
      <div className="flex items-center gap-2 mb-2">
        <UserIcon className="w-4 h-4 text-gray-400" />
        <span className="text-sm text-gray-700">{invoice.client_name}</span>
      </div>

      {/* Date */}
      <div className="flex items-center gap-2 mb-3">
        <CalendarIcon className="w-4 h-4 text-gray-400" />
        <span className="text-sm text-gray-500">{formatDate(invoice.invoice_date)}</span>
      </div>

      {/* Amount - Prominent */}
      <div className="text-right">
        <span className="text-xl font-bold text-[#5B8E87]">
          {formatCurrency(invoice.total_amount)}
        </span>
      </div>

      {/* Service Description - Truncated */}
      {invoice.service_description && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          <p className="text-xs text-gray-500 truncate">{invoice.service_description}</p>
        </div>
      )}
    </button>
  );
}
